import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Heart, MessageCircle, Share2, Send } from 'lucide-react';
import { getApiUrl, getAssetUrl } from '../config/config'; 

// ==========================================
// STYLED COMPONENTS
// ==========================================
const PageContainer = styled.div`min-height: 100vh; background: #000; color: #fff; padding-bottom: 60px;`;
const Header = styled.header`background: #111; border-bottom: 1px solid #333; padding: 16px 24px; display: flex; align-items: center; position: sticky; top: 0; z-index: 100;`;
const BackButton = styled.button`background: none; border: none; color: #e0c16f; display: flex; align-items: center; gap: 8px; cursor: pointer; font-weight: bold; font-size: 1rem; &:hover { color: #fff; }`;
const ContentWrapper = styled.div`max-width: 800px; margin: 40px auto; padding: 0 20px;`;
const PostBox = styled.div`background: #111; border: 1px solid #333; border-radius: 16px; padding: 32px;`;
const PostTitle = styled.h1`font-size: 2rem; margin-top: 0; margin-bottom: 16px; color: #fff;`;
const PostContent = styled.p`font-size: 1.1rem; line-height: 1.6; color: #ccc; margin-bottom: 32px; white-space: pre-wrap;`;
const PostImage = styled.img`width: 100%; max-height: 500px; object-fit: cover; border-radius: 12px; margin-bottom: 32px; border: 1px solid #333;`;

const ActionBar = styled.div`display: flex; gap: 24px; border-top: 1px solid #333; border-bottom: 1px solid #333; padding: 16px 0; margin-bottom: 32px;`;
const ActionBtn = styled.button`background: none; border: none; color: ${props => props.active ? '#ff4d4d' : '#888'}; display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 1rem; transition: 0.2s; &:hover { color: ${props => props.active ? '#ff4d4d' : '#e0c16f'}; }`;

const CommentsSection = styled.div`margin-top: 20px;`;
const CommentInputBox = styled.div`display: flex; gap: 12px; margin-bottom: 32px;`;
const CommentInput = styled.input`flex: 1; background: #000; border: 1px solid #333; border-radius: 8px; padding: 12px 16px; color: #fff; outline: none; &:focus { border-color: #e0c16f; }`;
const SendBtn = styled.button`background: #e0c16f; color: #000; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; &:hover { background: #bd851b; }`;

const CommentCard = styled.div`background: #000; padding: 16px; border-radius: 8px; margin-bottom: 12px; border: 1px solid #222; display: flex; flex-direction: column; gap: 4px;`;
const CommentAuthor = styled.strong`color: #e0c16f; font-size: 0.95rem;`;
const CommentText = styled.span`color: #ccc; font-size: 0.95rem; line-height: 1.4;`;

// ==========================================
// MAIN COMPONENT
// ==========================================
const SinglePost = () => {
  const { username, postId } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  useEffect(() => {
    fetchRealPostData();
  }, [postId]);

  const fetchRealPostData = async () => {
    try {
      setLoading(true);
      const baseUrl = getApiUrl();
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // ISPRAVLJENO: Koristi se /post/ umesto /posts/
      const postRes = await fetch(`${baseUrl}/creator-content/post/${postId}`, { headers });
      if (!postRes.ok) throw new Error('Post not found');
      const postData = await postRes.json();
      
      // ISPRAVLJENO: Koristi se /post/ umesto /posts/
      const commentsRes = await fetch(`${baseUrl}/feed/comments/${postId}`, { headers });
      let commentsData = [];
      if (commentsRes.ok) {
        const resJson = await commentsRes.json();
        commentsData = resJson.comments || resJson || [];
      }

      setPost(postData.post || postData); 
      setLikesCount(postData.post?.likes_count || postData.likes_count || 0);
      setComments(commentsData);

      if (token) {
        const statusRes = await fetch(`${baseUrl}/feed/like/${postId}/status`, { headers });
        if (statusRes.ok) setIsLiked((await statusRes.json()).liked);
      }
      
    } catch (err) {
      console.error("Error fetching post:", err);
      setError("Failed to load post.");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    const token = localStorage.getItem('token');
    if (!token) return alert("You must be logged in to like posts.");

    try {
      const baseUrl = getApiUrl();
      const nextLiked = !isLiked;
      const res = await fetch(`${baseUrl}/feed/like/${postId}`, {
        method: nextLiked ? 'POST' : 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to update like');
      setIsLiked(data.liked);
      setLikesCount(data.likes_count);
    } catch (err) {
      console.error("Like error:", err);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    const token = localStorage.getItem('token');
    if (!token) return alert("You must be logged in to comment.");

    try {
      setCommentSubmitting(true);
      setCommentError('');
      const baseUrl = getApiUrl();
      // ISPRAVLJENO: Koristi se /post/ umesto /posts/
      const res = await fetch(`${baseUrl}/feed/comment/${postId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: commentText })
      });

      if (res.ok) {
        const newComment = await res.json();
        setComments([newComment.comment || newComment, ...comments]);
        setCommentText(''); 
      } else {
        const data = await res.json().catch(() => ({}));
        setCommentError(data.error || 'Failed to post comment.');
      }
    } catch (err) {
      console.error("Comment error:", err);
      setCommentError('Failed to post comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  if (loading) return <PageContainer><div style={{textAlign:'center', marginTop:'100px', color: '#e0c16f'}}>Loading actual post...</div></PageContainer>;
  if (error || !post) return <PageContainer><div style={{textAlign:'center', marginTop:'100px', color: '#ff4d4d'}}>Post not found.</div></PageContainer>;

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate(`/${username}`)}>
          <ArrowLeft size={20} /> Back to Profile
        </BackButton>
      </Header>

      <ContentWrapper>
        <PostBox>
          <PostTitle>{post.title}</PostTitle>
          {post.image_url && <PostImage src={getAssetUrl(post.image_url)} alt="Post Image" />}
          <PostContent>{post.content}</PostContent>

          <ActionBar>
            <ActionBtn active={isLiked} onClick={handleLike}>
              <Heart size={20} fill={isLiked ? '#ff4d4d' : 'none'} /> {likesCount} Likes
            </ActionBtn>
            <ActionBtn>
              <MessageCircle size={20} /> {comments.length} Comments
            </ActionBtn>
            <ActionBtn onClick={() => navigator.clipboard.writeText(window.location.href).then(() => alert("Link copied!"))}>
              <Share2 size={20} /> Share
            </ActionBtn>
          </ActionBar>

          <CommentsSection>
            <h3 style={{marginBottom: '20px'}}>Comments</h3>
            {commentError && <p style={{color:'#ff8b96', marginBottom:'12px'}}>{commentError}</p>}
            <CommentInputBox>
              <CommentInput 
                placeholder="Write a comment..." 
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} 
              />
              <SendBtn onClick={handleAddComment} disabled={commentSubmitting || !commentText.trim()}><Send size={18} /></SendBtn>
            </CommentInputBox>

            {comments.length === 0 ? <p style={{color: '#666'}}>No comments yet. Be the first!</p> : comments.map((c, index) => (
              <CommentCard key={c.id || index}>
                <CommentAuthor>@{c.username || c.user || "user"}</CommentAuthor>
                <CommentText>{c.text || c.content}</CommentText>
              </CommentCard>
            ))}
          </CommentsSection>

        </PostBox>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SinglePost;
